import type {
  ContractManagementViewColumnQuestionCreate,
  EnumResponseTypeValue,
  ExtractionPromptQuestion,
} from '@/api';
import type { LinkedPrompt } from '@/types/contractManagement/columnPreview';

/** The question fields of the column drawers' forms. */
export interface QuestionFormFields {
  prompt: string;
  /** A plain response type, or 'enum' / 'multiple_enum' for a choice. */
  responseType: string;
  /** Free text: JSON, or a plain string. */
  exampleData: string;
  enumOptions: EnumResponseTypeValue[];
}

export function isEnumResponseType(responseType: string): boolean {
  return responseType === 'enum' || responseType === 'multiple_enum';
}

/** Why the form's choice options can't be saved; null when they can (or it isn't a choice). */
export function enumOptionsError(form: QuestionFormFields): string | null {
  if (!isEnumResponseType(form.responseType)) return null;
  if (form.enumOptions.length < 2) return 'At least two options are required for choice types';
  if (form.enumOptions.some(option => !option.name.trim())) return 'All options must have a name';
  const names = form.enumOptions.map(option => option.name);
  if (new Set(names).size !== names.length) return 'All option names must be unique';
  return null;
}

function readsAsJson(text: string): boolean {
  try {
    JSON.parse(text);
    return true;
  } catch {
    return false;
  }
}

/** Example data as text that formToQuestion reads back as the same value. */
function formatExampleData(value: unknown): string {
  // A string that reads as JSON ("42", "true", "null") keeps its quotes, or saving would turn
  // it into a number, boolean or null.
  if (typeof value === 'string') return readsAsJson(value.trim()) ? JSON.stringify(value) : value;
  return typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint'
    ? String(value)
    : JSON.stringify(value, null, 2);
}

/** A question as form fields; enum options are copied so editing them leaves the source alone. */
export function promptToForm(question: ExtractionPromptQuestion): QuestionFormFields {
  const responseType = question.response_type;
  const isEnum = typeof responseType === 'object';
  return {
    prompt: question.prompt,
    responseType: isEnum
      ? responseType.multiple_choices
        ? 'multiple_enum'
        : 'enum'
      : responseType,
    exampleData:
      question.example_data === undefined || question.example_data === null
        ? ''
        : formatExampleData(question.example_data),
    enumOptions: isEnum
      ? responseType.values.map(value => ({
          name: value.name,
          definition: value.definition,
          color: value.color ?? null,
        }))
      : [],
  };
}

/** The form's question. Example data that isn't valid JSON is kept as a string. */
export function formToQuestion(form: QuestionFormFields): ExtractionPromptQuestion {
  const exampleText = form.exampleData.trim();
  let exampleData: unknown = null;
  if (exampleText) {
    try {
      exampleData = JSON.parse(exampleText);
    } catch {
      exampleData = exampleText;
    }
  }

  const isEnum = isEnumResponseType(form.responseType);
  return {
    prompt: form.prompt.trim(),
    response_type: isEnum
      ? {
          multiple_choices: form.responseType === 'multiple_enum',
          values: form.enumOptions.map(option => ({
            name: option.name,
            definition: option.definition || '',
            color: option.color,
          })),
        }
      : (form.responseType as Exclude<ExtractionPromptQuestion['response_type'], object>),
    example_data: exampleData,
  };
}

/**
 * Whether the form still asks *question*. Both sides go through the form round trip, so
 * formatting differences (example data indentation, option colours) don't count as edits.
 */
export function sameQuestion(
  form: QuestionFormFields,
  question: ExtractionPromptQuestion
): boolean {
  return (
    JSON.stringify(formToQuestion(form)) === JSON.stringify(formToQuestion(promptToForm(question)))
  );
}

/**
 * A column question for the API: the linked prompt's uid, or a new question with its types and
 * its prompt's name (none: the prompt is named after the column).
 */
export function columnQuestion(
  question: ExtractionPromptQuestion,
  linkedPrompt: LinkedPrompt | null | undefined,
  contractTypeUids: string[] = [],
  promptName?: string
): ContractManagementViewColumnQuestionCreate {
  if (linkedPrompt) return { extraction_prompt_uid: linkedPrompt.uid };
  return {
    ...question,
    ...(promptName && { name: promptName }),
    contract_type_uids: contractTypeUids,
  };
}

/** A response type's short label, for tables. */
export function responseTypeLabel(responseType: ExtractionPromptQuestion['response_type']): string {
  if (typeof responseType === 'object') {
    return responseType.multiple_choices ? 'Multi Choice' : 'Choice';
  }

  const typeMap: Record<string, string> = {
    str: 'Text',
    'list[str]': 'Text List',
    int: 'Integer',
    'list[int]': 'Int List',
    float: 'Float',
    'list[float]': 'Float List',
    number: 'Number',
    'list[number]': 'Num List',
    bool: 'Yes/No',
    'list[bool]': 'Bool List',
    date: 'Date',
    'list[date]': 'Date List',
    datetime: 'Date & Time',
    'list[datetime]': 'Date & Time List',
    CurrencyAmount: 'Currency',
    'list[CurrencyAmount]': 'Curr List',
    Entity: 'Entity',
    'list[Entity]': 'Entity List',
    CalendarEvents: 'Calendar',
  };

  return typeMap[responseType] || responseType;
}
